import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Interweave from 'interweave'
import { isEmpty, sortBy } from 'lodash-es/'
import { convertMdToHtml } from '..'
import {
  FINDING_HTML_ID_ATTRIBUTE,
  OTHER_TITLE_ID,
  SEVERITY,
  SEVERITY_DESCRIPTION,
} from '../../../constants'
import { getSeverityLabelType, getStatusLabelType } from '../utils'
import Table from '../../Table'
import Section from '../../Section'
import Link from '../../Link'
import Label from '../../Label'
import Badge from '../../Badge'

const StyledTable = styled(Table)`
  a {
    font-weight: 600;
  }

  ${Table.Row} ${Table.Data}:first-child {
    white-space: nowrap;
  }

  @media screen and (max-width: 1200px) {
    border: 0;
    padding: 0;
    box-shadow: unset;

    table,
    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    thead tr {
      position: absolute;
      opacity: 0;
    }

    ${Table.Row} {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.05);
      border-radius: 12px;
      padding: 5px 12px;
      margin-bottom: 20px;
    }

    ${Table.Data} {
      border: none;
      border-bottom: 1px solid #e5e5e5;
      position: relative;
      padding-left: 50%;

      &:before {
        position: absolute;
        left: 6px;
        white-space: nowrap;
        font-weight: 700;
        text-transform: uppercase;
      }

      &:last-child {
        border-bottom: 0;
      }

      &:nth-of-type(1):before {
        content: 'Id';
      }

      &:nth-of-type(2):before {
        content: 'Description';
      }

      &:nth-of-type(3):before {
        content: 'Severity';
      }

      &:nth-of-type(4):before {
        content: 'Status';
      }
    }
  }
`

const StyledTableHeader = styled(Table.Header)`
  letter-spacing: 0.05em;
  text-transform: uppercase;
  min-width: 55px;
`

const SummaryOfFindings = ({ report: { reportContent } }) => (
  <Section title="Summary of Findings">
    <Interweave content={convertMdToHtml.makeHtml(reportContent.summary.overallAssessment)} />
    {!isEmpty(reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray) && (
      <StyledTable>
        <Table.THead>
          <Table.Row>
            <StyledTableHeader>ID</StyledTableHeader>
            <StyledTableHeader>Description</StyledTableHeader>
            <StyledTableHeader>Severity</StyledTableHeader>
            <StyledTableHeader>Status</StyledTableHeader>
          </Table.Row>
        </Table.THead>
        <Table.TBody>
          {(reportContent.breakdownAndAutomatedAnalysis.sortBySeverity
            ? sortBy(reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray, v =>
                parseInt(v.severity.value, 10),
              )
            : reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray
          ).map((vulnerability, index) => {
            const findingIdLetters = reportContent.breakdownAndAutomatedAnalysis.vulnIndex || 'QS'
            const findingId = index + 1

            return (
              <Table.Row key={findingId}>
                <Table.Data>
                  <Link href={`#${FINDING_HTML_ID_ATTRIBUTE}${findingId}`}>
                    {findingIdLetters}-{findingId}
                  </Link>
                </Table.Data>
                <Table.Data>
                  <Interweave
                    content={convertMdToHtml
                      .makeHtml(
                        Number(vulnerability.title.value) !== OTHER_TITLE_ID
                          ? vulnerability.title.label
                          : vulnerability.otherTitle,
                      )
                      .replace(/(<p>.+<\/p>)/g, '<b>$&</b>')}
                  />
                </Table.Data>
                <Table.Data>
                  <Label
                    type={getSeverityLabelType(vulnerability.severity.value)}
                    tooltip={{
                      id: `${findingId}-${vulnerability.title.label}`,
                      content: SEVERITY_DESCRIPTION[vulnerability.severity.value],
                    }}
                  >
                    {SEVERITY[vulnerability.severity.value].label}
                  </Label>
                </Table.Data>
                <Table.Data>
                  {vulnerability.status ? (
                    <Badge type={getStatusLabelType(vulnerability.status.value)}>
                      {vulnerability.status.label}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </Table.Data>
              </Table.Row>
            )
          })}
        </Table.TBody>
      </StyledTable>
    )}
  </Section>
)

SummaryOfFindings.propTypes = {
  report: PropTypes.object,
}

export default SummaryOfFindings
